// ═══════════════════════════════════════════════════════════════
// [A15-05] Które adresy GET wolno trzymać w 60-sekundowym cache klienta.
//
// CO BYŁO ZLE: dopasowanie wzorca przez `url.includes(p)` — czyli po PODCIĄGU adresu,
// a nie po ścieżce. Wzorzec "/admin/users" łapał więc także "/admin/users/5",
// "/admin/users/5/permissions", "/admin/users/5/logs", "/admin/users/5/block-info"
// i "/admin/users/5/configurations"; "/itam/devices" łapał "/itam/devices/{id}",
// "/itam/devices/{id}/sim-card" oraz "/itam/devices/check-inventory";
// "/dynamic-settings/drives" łapał "/dynamic-settings/drives/{id}/users",
// "…/master-addresses" i "…/assigned-users"; a "/dynamic-settings/address-book"
// łapał zupełnie inny endpoint — wyszukiwarkę "/dynamic-settings/address-book-search"
// oraz "/dynamic-settings/address-book/by-email".
//
// SKUTEK DLA UŻYTKOWNIKA: karta pojedynczego użytkownika, jego uprawnienia, logi,
// szczegóły sprzętu, obsada dysku czy wynik wyszukiwania w książce adresowej mogły
// przez minutę pokazywać STAN SPRZED ZMIANY — np. po zmianie wprowadzonej przez innego
// administratora albo w innej karcie przeglądarki. Sprawdzenie wolnego numeru
// inwentarzowego (check-inventory) także odpowiadało z cache.
//
// DLACZEGO TAK NAPRAWIONE: cache został pomyślany dla DUŻYCH, RZADKO ZMIENNYCH LIST
// (patrz komentarz przy jego wprowadzeniu), a nie dla ich pod-zasobów. Porównujemy więc
// pełną ŚCIEŻKĘ (po odcięciu query stringu i normalizacji końcowego „/"), zamiast szukać
// podciągu. Wszystkie listy, które były cache'owane celowo, są cache'owane dalej —
// wołane są dokładnie tymi ścieżkami, a parametry lecą w `config.params`.
// ═══════════════════════════════════════════════════════════════

/** Ścieżki (DOKŁADNE, nie prefiksy) list, których odpowiedzi wolno trzymać w cache. */
export const CACHEABLE_PATHS = [
    "/departments/groupped",
    "/departments/all",
    "/dynamic-settings/address-book",
    "/admin/users",
    "/itam/devices",
    "/dynamic-settings/drives",
];

/** Adres → sama ścieżka: bez fragmentu, bez query stringu, bez końcowego „/". */
const toPath = (url) => {
    if (typeof url !== "string") return "";
    let path = url.split("#")[0].split("?")[0].trim();
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return path;
};

/** true tylko dla DOKŁADNEJ ścieżki listy — pod-zasoby (…/{id}, …/{id}/coś) nigdy. */
export const isCacheableUrl = (url) => {
    const path = toPath(url);
    return path !== "" && CACHEABLE_PATHS.some((p) => p === path);
};

// ═══════════════════════════════════════════════════════════════
// [A15-11] Ścieżki sklejane TYLKO NA CZAS LOTU ŻĄDANIA — bez 60-sekundowego cache.
//
// BYŁO ZLE: widoczność modułów pobierają NIEZALEŻNIE od siebie trzy miejsca naraz przy każdym
// ustawieniu zalogowanego użytkownika — AuthContext (blokada tras), Sidebar (menu boczne)
// i UIContext (widget „Nowe zgłoszenie"). Adres '/module-visibility' nie jest listą cache'owaną,
// więc do serwera leciały trzy identyczne zapytania na każde wejście do aplikacji i każde F5.
// SKUTEK DLA UŻYTKOWNIKA: przy kilkuset zalogowanych osobach to potrójne, zbędne obciążenie bazy
// w godzinach szczytu — czyli wolniejsza cała aplikacja dla wszystkich.
//
// DLACZEGO NIE ZWYKŁY CACHE: 60-sekundowy cache oznaczałby, że po przestawieniu przełącznika
// przez administratora moduł znika albo pojawia się dopiero po minucie — dokładnie ten rodzaj
// „ekran pokazuje stan sprzed zmiany", który opisuje komentarz wyżej.
// DLATEGO: sklejamy wyłącznie żądania będące W LOCIE. Trzech równoległych konsumentów dostaje
// jedną odpowiedź (jedno zapytanie zamiast trzech), a każde KOLEJNE wywołanie idzie do serwera
// na świeżo — zmiana ustawienia widać natychmiast, tak samo jak przed naprawą.
// ═══════════════════════════════════════════════════════════════

/** Ścieżki (DOKŁADNE), dla których scalamy równoległe żądania, ale NIE trzymamy odpowiedzi. */
export const DEDUP_ONLY_PATHS = [
    "/module-visibility",
];

/** true tylko dla DOKŁADNEJ ścieżki z listy dedupowanych. */
export const isDedupOnlyUrl = (url) => {
    const path = toPath(url);
    return path !== "" && DEDUP_ONLY_PATHS.some((p) => p === path);
};
